// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN USERS — rendu d'une ligne du tableau utilisateurs (pur)
// Extrait de page-admin-users.ts (découpe god-function `renderUsersPage` 2026-07) :
// rendu PUR d'une ligne (aucun état de closure — `teamMap` immuable threadé).
// Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, isUserActive.

import type { UserEntity } from './page-admin-users.js';

/** `<tr>` d'un utilisateur : trigramme, nom (patronyme), prénom, type,
 * interne/externe, actif, rôle, équipes (via `teamMap`), email, actions.
 *
 * Août 2026 : les colonnes Nom/Prénom affichent `last_name` / `first_name` et
 * non plus `name`. Une équipe (ou un compte antérieur au découpage) n'a ni
 * l'un ni l'autre : on retombe alors sur `name` dans la colonne Nom, pour ne
 * jamais présenter une ligne anonyme. */
/**
 * `canWrite` conditionne les cellules cliquables (qui ouvrent la fiche en
 * édition) et les boutons ✎ / ✕. Passé en paramètre plutôt que lu depuis
 * `window` pour garder le fragment pur et testable. Défaut `true` : le
 * comportement historique, que fige `page-users-row.test.ts`.
 */
export function buildUserRowHtml(
  u: UserEntity,
  teamMap: Record<number, string>,
  canWrite: boolean = true,
): string {
  const teamsLabel =
    !u.is_team && u.team_ids && u.team_ids.length
      ? u.team_ids
          .map(
            (id) =>
              `<span style="background:var(--accent-bg);color:var(--accent);font-size:10px;padding:1px 6px;border-radius:8px;margin-right:2px">${esc(teamMap[id] || '?')}</span>`,
          )
          .join('')
      : '—';
  const typeLabel = u.is_team
    ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:10px;padding:1px 6px;border-radius:8px;font-weight:500">${esc(t('users.type.team'))}</span>`
    : `<span style="color:var(--text3);font-size:12px">${esc(t('users.type.individual'))}</span>`;
  const extLabel = u.is_external
    ? `<span style="background:#fef9c3;color:#854d0e;font-size:10px;padding:1px 6px;border-radius:8px">${esc(t('users.label.external'))}</span>`
    : `<span style="color:var(--text3);font-size:12px">${esc(t('users.label.internal'))}</span>`;
  const roleLabel = u.role
    ? `<span style="font-size:11px;color:var(--text3)">${esc(u.role)}</span>`
    : '—';
  const activeLabel = u.is_team
    ? '<span style="color:var(--text3);font-size:12px">—</span>'
    : isUserActive(u as never)
      ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:10px;padding:1px 6px;border-radius:8px;font-weight:500">${esc(t('users.active.yes'))}</span>`
      : `<span style="background:#fee2e2;color:#b91c1c;font-size:10px;padding:1px 6px;border-radius:8px;font-weight:500">${esc(t('users.active.no'))}</span>`;
  const lastNameLabel = u.last_name || u.name || '—';
  const clickable = canWrite ? `data-click-user="${u.id}"` : '';
  const cursor = canWrite ? 'pointer' : 'default';
  return `<tr>
      <td style="cursor:${cursor}" ${clickable}><span class="text-mono" style="font-weight:600;background:var(--bg3);padding:2px 6px;border-radius:4px;font-size:12px;${canWrite ? 'text-decoration:underline' : ''}">${esc(u.trigram || '—')}</span></td>
      <td style="cursor:${cursor}" ${clickable}><strong>${esc(lastNameLabel)}</strong></td>
      <td style="cursor:${cursor}" ${clickable}>${esc(u.first_name || '—')}</td>
      <td>${typeLabel}</td>
      <td>${extLabel}</td>
      <td>${activeLabel}</td>
      <td>${roleLabel}</td>
      <td style="font-size:12px">${teamsLabel}</td>
      <td>${esc(u.email || '—')}</td>
      <td>${
        canWrite
          ? `<button class="btn btn-secondary btn-icon" data-edit-user="${u.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-user="${u.id}" data-user-name="${esc(u.name)}" style="margin-left:4px" title="${esc(t('common.delete'))}">✕</button>`
          : ''
      }</td>
    </tr>`;
}
